import React, { useEffect, useState } from "react";
import { Container, Card, CardBody, Label, Input, FormGroup, Button, Row, Col } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import LoadingSpinner from "../../components/LoadingSpinner";
import { GetAppConfig, UpdateAppConfig } from "../../requests";
import { toast } from "react-toastify";

const AppSettings = () => {
  document.title = "App Settings | Susipizza";

  const [loading, setLoading] = useState(false);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [minSupportedVersion, setMinSupportedVersion] = useState("0.0.0");
  const [latestVersion, setLatestVersion] = useState("0.0.0");
  const [updateMessage, setUpdateMessage] = useState("");
  const [updateUrl, setUpdateUrl] = useState("");

  const loadConfig = async () => {
    try {
      setLoading(true);
      const resp = await GetAppConfig();
      const config = resp.data;
      setMaintenanceEnabled(!!config.maintenanceMode?.enabled);
      setMaintenanceMessage(config.maintenanceMode?.message || "");
      setMinSupportedVersion(config.minSupportedVersion || "0.0.0");
      setLatestVersion(config.latestVersion || "0.0.0");
      setUpdateMessage(config.updateMessage || "");
      setUpdateUrl(config.updateUrl || "");
    } catch (err) {
      console.error(err);
      toast.error("Failed to load app settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await UpdateAppConfig(token, {
        maintenanceMode: { enabled: maintenanceEnabled, message: maintenanceMessage },
        minSupportedVersion,
        latestVersion,
        updateMessage,
        updateUrl,
      });
      toast.success("App settings updated successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update app settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="susipizza" breadcrumbItem="App Settings" />

          <Card>
            <CardBody>
              <h5 className="mb-3">Maintenance Mode</h5>
              <p className="text-muted fs-13">
                When enabled, every customer sees a blocking maintenance
                screen on app launch instead of the app.
              </p>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label for="maintenanceEnabled">Maintenance Mode</Label>
                    <Input
                      id="maintenanceEnabled"
                      type="select"
                      value={maintenanceEnabled.toString()}
                      onChange={(e) => setMaintenanceEnabled(e.target.value === "true")}
                    >
                      <option value="false">Off</option>
                      <option value="true">On</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="8">
                  <FormGroup>
                    <Label for="maintenanceMessage">Message shown to customers</Label>
                    <Input
                      id="maintenanceMessage"
                      type="textarea"
                      value={maintenanceMessage}
                      onChange={(e) => setMaintenanceMessage(e.target.value)}
                      placeholder="We're currently performing scheduled maintenance. Please check back shortly."
                    />
                  </FormGroup>
                </Col>
              </Row>

              <hr className="my-4" />

              <h5 className="mb-3">App Version Gate</h5>
              <p className="text-muted fs-13">
                Customers on an app version below "Minimum Supported Version"
                are blocked with an update-required screen until they update.
                Keep this in sync with what you actually publish.
              </p>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label for="minVersion">Minimum Supported Version</Label>
                    <Input
                      id="minVersion"
                      type="text"
                      value={minSupportedVersion}
                      onChange={(e) => setMinSupportedVersion(e.target.value)}
                      placeholder="e.g. 1.2.0"
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="latestVersion">Latest Available Version</Label>
                    <Input
                      id="latestVersion"
                      type="text"
                      value={latestVersion}
                      onChange={(e) => setLatestVersion(e.target.value)}
                      placeholder="e.g. 1.3.0"
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="updateUrl">Update URL (Play Store / App Store link)</Label>
                    <Input
                      id="updateUrl"
                      type="text"
                      value={updateUrl}
                      onChange={(e) => setUpdateUrl(e.target.value)}
                      placeholder="https://play.google.com/store/apps/details?id=..."
                    />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label for="updateMessage">Update-required message</Label>
                <Input
                  id="updateMessage"
                  type="textarea"
                  value={updateMessage}
                  onChange={(e) => setUpdateMessage(e.target.value)}
                  placeholder="A new version of the app is required to continue."
                />
              </FormGroup>

              <Button color="primary" onClick={handleSave} disabled={loading}>
                Save Settings
              </Button>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AppSettings;
